import { Char, Expression, TokenType } from "@evergis/condition";
import { AttributeType } from "@evergis/api";
import { ComparisonOperator } from "@evergis/ui";
import { isNil } from "lodash";

import {
  BETWEEN_OPERATORS,
  CONTAINS_OPERATORS,
  ENDS_WITH_OPERATORS,
  STARTS_WITH_OPERATORS,
} from "../../../components/LayerFilter/constants";

import { OPERATOR_CONDITION_REMAP } from "./ComparisonOperator";
import { DateFormat, formatDate } from "../../../utils/date";

import { FieldTypes } from "../../../interfaces/service";
import { FilterConditionType } from "../../../components/LayerFilter/types";

export const comparisonOperatorMapReversed: Record<string, ComparisonOperator> = {
  [TokenType.ArEq]: ComparisonOperator.Equals,
  [TokenType.ArNotEq]: ComparisonOperator.NotEquals,
  [TokenType.ArGr]: ComparisonOperator.GreaterThan,
  [TokenType.ArLs]: ComparisonOperator.LessThan,
  [TokenType.ArGre]: ComparisonOperator.GreaterOrEquals,
  [TokenType.ArLse]: ComparisonOperator.LessOrEquals,
  [TokenType.Between]: ComparisonOperator.Between,
  [TokenType.Outside]: ComparisonOperator.Outside,
};

export const NUMERIC_ATTRIBUTE_TYPES: FieldTypes[] = [
  AttributeType.Int32,
  AttributeType.Int64,
  AttributeType.DateTime,
  AttributeType.Double,
];

const formatValue = (value: string | number, isDate?: boolean) => {
  return isDate
    ? `#'${formatDate(value, {
        dateFormat: DateFormat.UTC,
        defaultValue: value,
      })}'`
    : value
    ? value.toString()
    : 0;
};

export function formatCondition(condition: FilterConditionType) {
  const { comparisonOperator, value, type, attribute } = condition;
  const operator = OPERATOR_CONDITION_REMAP[comparisonOperator] || comparisonOperator;
  const preparedValue = typeof value === "string" && value.includes(" - ") ? value.split(" - ") : value;
  const isDate = type === AttributeType.DateTime;
  const isBetweenOutside = BETWEEN_OPERATORS.includes(comparisonOperator);
  const escapeValue = !NUMERIC_ATTRIBUTE_TYPES.includes(type) && preparedValue !== null;

  if (typeof value === "string" && value.startsWith("@")) {
    return `${attribute} ${operator} ${value}`;
  }

  if (isBetweenOutside && preparedValue && preparedValue[0] !== undefined) {
    return `${OPERATOR_CONDITION_REMAP[comparisonOperator]}(${attribute}, ${formatValue(
      preparedValue[0],
      isDate,
    )}, ${formatValue(preparedValue[1], isDate)})`;
  }

  const formattedValue = Array.isArray(preparedValue)
    ? preparedValue.map(val => formatValue(val, isDate))
    : formatValue(preparedValue, isDate);

  const nulledValue = isNil(preparedValue) ? "{null}" : formattedValue;

  const valueEscaped =
    escapeValue && typeof nulledValue === "string"
      ? `'${Char.escapeSpecChars(addOperationToValue(nulledValue, comparisonOperator))}'`
      : nulledValue;

  return `${attribute} ${operator} ${valueEscaped}`;
}

export function createConditionString(condition: FilterConditionType) {
  if (Array.isArray(condition)) {
    return `( ${formatCondition(condition[0])} ${condition[0].operation} ${formatCondition(condition[1])} )`;
  }

  return formatCondition(condition);
}

export function createCondition({ attribute, operation, value }: Expression) {
  const comparisonOperator = comparisonOperatorMapReversed[operation];
  const convertedResult = convertOperatorValue(value, comparisonOperator);

  return {
    attribute,
    ...convertedResult,
  } as FilterConditionType;
}

function convertOperatorValue(value: string | number | boolean, comparisonOperator: ComparisonOperator) {
  if (typeof value === "string") {
    const operatorIndex = +(comparisonOperator === ComparisonOperator.NotEquals);

    if (value.startsWith(Char.PERCENTAGE) && value.endsWith(Char.PERCENTAGE)) {
      return {
        value: value.slice(1, -1),
        comparisonOperator: CONTAINS_OPERATORS[operatorIndex],
      };
    }

    if (value.startsWith(Char.PERCENTAGE)) {
      return {
        value: value.slice(1),
        comparisonOperator: ENDS_WITH_OPERATORS[operatorIndex],
      };
    }

    if (value.endsWith(Char.PERCENTAGE)) {
      return {
        value: value.slice(0, -1),
        comparisonOperator: STARTS_WITH_OPERATORS[operatorIndex],
      };
    }
  }

  return { value, comparisonOperator };
}

const addContains = (value: string | number) => `%${value}%`;
const addStartsWith = (value: string | number) => `${value}%`;
const addEndsWith = (value: string | number) => `%${value}`;

function addOperationToValue(value: string, operator: ComparisonOperator) {
  switch (operator) {
    case ComparisonOperator.Contains:
    case ComparisonOperator.NotContains:
      return addContains(value);
    case ComparisonOperator.StartsWith:
    case ComparisonOperator.NotStartsWith:
      return addStartsWith(value);
    case ComparisonOperator.EndsWith:
    case ComparisonOperator.NotEndsWith:
      return addEndsWith(value);
    case ComparisonOperator.Between:
      return Char.BETWEEN;
    case ComparisonOperator.Outside:
      return Char.OUTSIDE;
    default:
      return value;
  }
}
